import MuxPlayer from '@mux/mux-player-react';
import {
  effectiveAgeRange,
  effectiveLanguage,
  effectiveParticipation,
  type ActivityLanguageValue,
  type ParticipationValue,
} from '@yoyoclub/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { BilingualField } from '../../components/BilingualField';
import { DescriptionEditor } from '../../components/DescriptionEditor';
import AdminLayout, { type NavItem } from '../../components/admin/AdminLayout';
import {
  bilingualPayload,
  emptyBilingual,
  hasBilingualValue,
  toBilingualValue,
  type BilingualValue,
} from '../../components/bilingualValue';
import { TOUR, tourAttr, useHostTourStore } from '../../features/hostTour';
import { useAuth } from '../../hooks/useAuth';
import { bilingualLines, localize } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import {
  courseApi,
  type AdminHostOption,
  type HostVideo,
  type PreviewVideo,
} from '../../services/courseApi';

const hostNavItems: NavItem[] = [
  { to: '/host/activities', label: 'Activities' },
  { to: '/host/courses', label: 'Courses' },
  { to: '/host/passes', label: 'Passes' },
  { to: '/host/bookings', label: 'Bookings' },
  { to: '/host/email', label: 'Email' },
];

// Strict parse: reject anything that is not a clean decimal (so "49usd",
// "1,000", "300元" surface as errors instead of saving a wrong amount).
const parseMoneyToCents = (value: string): number | null => {
  const s = value.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  return Math.round(parseFloat(s) * 100);
};
const toDollars = (cents: number | null): string => (cents == null ? '' : (cents / 100).toFixed(2));

export default function HostCourseEditor() {
  const siteLanguage = useSiteLanguage();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { getToken, user } = useAuth();

  const [title, setTitle] = useState<BilingualValue>(emptyBilingual());
  const [description, setDescription] = useState<BilingualValue>(emptyBilingual());
  const [priceUsd, setPriceUsd] = useState('');
  const [priceCny, setPriceCny] = useState('');
  const [status, setStatus] = useState<string>('');
  const [slug, setSlug] = useState<string>('');

  const [videos, setVideos] = useState<HostVideo[]>([]);
  const [canActivate, setCanActivate] = useState(false);
  const [newTitle, setNewTitle] = useState<BilingualValue>(emptyBilingual());
  // Lesson rename (PRD §4.5): the lesson being renamed and its draft title.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState<BilingualValue>(emptyBilingual());
  const [savingRename, setSavingRename] = useState(false);

  // Admin-only: pick which host owns a newly created course.
  const [hostOptions, setHostOptions] = useState<AdminHostOption[]>([]);
  const [hostId, setHostId] = useState('');

  // Courses are parent courses unless the host says otherwise (PRD §4.2); all required.
  const [participation, setParticipation] = useState<ParticipationValue>('ADULT_ONLY');
  const [language, setLanguage] = useState<ActivityLanguageValue>('ZH');
  const [ageMin, setAgeMin] = useState(isEdit ? '' : '18');
  const [ageMax, setAgeMax] = useState(isEdit ? '' : '99');
  // Detail-page images (stored on S3/CDN). Lesson videos go separately to Mux.
  const [images, setImages] = useState<{ imageUrl: string; isCover: boolean }[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingDescImage, setUploadingDescImage] = useState(false);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [addingSegment, setAddingSegment] = useState(false);
  // Per-lesson price override inputs (dollars), keyed by video id. Blank means
  // the lesson inherits the course default price.
  const [priceEdits, setPriceEdits] = useState<Record<string, { usd: string; cny: string }>>({});
  const [savingPriceId, setSavingPriceId] = useState<string | null>(null);
  // Owner/admin video preview (no purchase needed): token info keyed by videoId,
  // plus the lesson currently expanded in the player modal.
  const [previews, setPreviews] = useState<Record<string, PreviewVideo>>({});
  const [previewing, setPreviewing] = useState<{
    title: string;
    playbackId: string;
    token: string;
  } | null>(null);
  const muxEnvKey = import.meta.env.VITE_MUX_DATA_ENV_KEY as string | undefined;

  const loadVideos = useCallback(
    async (token: string) => {
      if (!id) return;
      const data = await courseApi.hostListVideos(id, token);
      setVideos(data.videos);
      setCanActivate(data.playability.canActivate);
      // Seed the override inputs from the saved per-lesson prices.
      setPriceEdits(
        Object.fromEntries(
          data.videos.map((v) => [v.id, { usd: toDollars(v.price), cny: toDollars(v.priceCny) }])
        )
      );
      // Preview tokens for the owning host (no entitlement needed); best-effort.
      try {
        const previewVideos = await courseApi.getPreviewPlayback(id, token);
        setPreviews(Object.fromEntries(previewVideos.map((p) => [p.videoId, p])));
      } catch {
        setPreviews({});
      }
    },
    [id]
  );

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const course = await courseApi.hostGetCourse(id as string, token);
        if (cancelled) return;
        setTitle(
          toBilingualValue({
            zh: course.titleCN,
            en: course.titleEN,
            autoLang: course.titleAutoLang,
          })
        );
        setDescription(
          toBilingualValue({
            zh: course.descriptionCN,
            en: course.descriptionEN,
            autoLang: course.descriptionAutoLang,
          })
        );
        setPriceUsd(toDollars(course.price));
        setPriceCny(toDollars(course.priceCny));
        setStatus(course.status);
        setSlug(course.slug);
        // Effective values: a course the backfill has not reached reads like it will after.
        const asRecorded = { ...course, activityType: 'RECORDED' };
        const age = effectiveAgeRange(asRecorded);
        setParticipation(effectiveParticipation(asRecorded));
        setLanguage(effectiveLanguage(asRecorded));
        setAgeMin(age.ageMin != null ? String(age.ageMin) : '');
        setAgeMax(age.ageMax != null ? String(age.ageMax) : '');
        setImages(course.images ?? []);
        await loadVideos(token);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load course');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, getToken, loadVideos]);

  // Admins create a course on behalf of a host; load the options and default
  // to the admin's own host profile when they have one.
  useEffect(() => {
    if (isEdit || !user?.isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const hosts = await courseApi.adminListHosts(token);
        if (cancelled) return;
        setHostOptions(hosts);
        const own = hosts.find((h) => h.email && h.email === user.email);
        setHostId(own?.id ?? '');
      } catch {
        // Non-fatal: the selector stays empty and a host must be chosen.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, user?.isAdmin, user?.email, getToken]);

  const saveCourse = async () => {
    setError(null);
    setNotice(null);
    if (!hasBilingualValue(title)) {
      setError('Enter a title in Chinese or English');
      return;
    }
    const usdCents = parseMoneyToCents(priceUsd);
    if (usdCents == null || usdCents <= 0) {
      setError('A positive USD price is required (numbers only, e.g. 49.00)');
      return;
    }
    // Optional CNY: a typo must surface as an error, not silently clear the
    // price (which would switch WeChat checkout back to USD).
    let cnyCents: number | null = null;
    if (priceCny.trim()) {
      cnyCents = parseMoneyToCents(priceCny);
      if (cnyCents == null || cnyCents <= 0) {
        setError('CNY price must be a positive number, or leave it blank');
        return;
      }
    }
    if (!isEdit && user?.isAdmin && !hostId) {
      setError('Select the host this course belongs to');
      return;
    }
    // Age is required: it decides which homepage stage shows the course.
    if (ageMin.trim() === '' || ageMax.trim() === '') {
      setError('Enter the age range (adults only: 18 to 99)');
      return;
    }
    const ageMinFinal = Number(ageMin);
    const ageMaxFinal = Number(ageMax);
    if (!Number.isInteger(ageMinFinal) || ageMinFinal < 0) {
      setError('Min age must be a whole number');
      return;
    }
    if (!Number.isInteger(ageMaxFinal) || ageMaxFinal < 0) {
      setError('Max age must be a whole number');
      return;
    }
    if (ageMinFinal > ageMaxFinal) {
      setError('Min age cannot be greater than max age');
      return;
    }
    setSaving(true);
    try {
      const token = await getToken();
      if (!token) return;
      const body = {
        ...bilingualPayload(title, 'title'),
        ...bilingualPayload(description, 'description'),
        price: usdCents,
        priceCny: cnyCents,
        images,
        ageMin: ageMinFinal,
        ageMax: ageMaxFinal,
        participation,
        language,
        ...(!isEdit && user?.isAdmin && hostId ? { hostId } : {}),
      };
      if (isEdit) {
        await courseApi.hostUpdateCourse(id as string, body, token);
        setNotice('Saved');
      } else {
        const created = await courseApi.hostCreateCourse(body, token);
        // The walkthrough's "Save the course first" step is done; resume at Lessons on the edit page.
        useHostTourStore.getState().advanceRunTo('courses', 'lessons');
        navigate(`/host/courses/${created.id}/edit`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addSegment = async () => {
    if (!id || !hasBilingualValue(newTitle) || addingSegment) return;
    setAddingSegment(true);
    try {
      const token = await getToken();
      if (!token) return;
      // The server assigns the order atomically; we still send max+1 as a hint.
      const nextOrder = videos.length ? Math.max(...videos.map((v) => v.order)) + 1 : 0;
      await courseApi.hostCreateVideoSlot(
        id,
        bilingualPayload(newTitle, 'title'),
        nextOrder,
        token
      );
      setNewTitle(emptyBilingual());
      await loadVideos(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add lesson');
    } finally {
      setAddingSegment(false);
    }
  };

  const startRename = (v: HostVideo) => {
    setRenamingId(v.id);
    setRenameDraft(toBilingualValue({ zh: v.titleCN, en: v.titleEN, autoLang: v.titleAutoLang }));
  };

  const saveRename = async () => {
    if (!id || !renamingId || savingRename) return;
    if (!hasBilingualValue(renameDraft)) {
      setError('Enter a lesson title in Chinese or English');
      return;
    }
    setSavingRename(true);
    try {
      const token = await getToken();
      if (!token) return;
      await courseApi.hostUpdateVideoTitle(
        id,
        renamingId,
        bilingualPayload(renameDraft, 'title'),
        token
      );
      setRenamingId(null);
      await loadVideos(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename lesson');
    } finally {
      setSavingRename(false);
    }
  };

  const savePrice = async (v: HostVideo) => {
    if (!id) return;
    const edit = priceEdits[v.id] ?? { usd: '', cny: '' };
    // Blank => clear the override (inherit the course default). A present value
    // must parse cleanly, mirroring the course-level price validation.
    let price: number | null = null;
    if (edit.usd.trim()) {
      price = parseMoneyToCents(edit.usd);
      if (price == null || price <= 0) {
        setError('Lesson USD price must be a positive number, or blank to use the course default');
        return;
      }
    }
    let priceCny: number | null = null;
    if (edit.cny.trim()) {
      priceCny = parseMoneyToCents(edit.cny);
      if (priceCny == null || priceCny <= 0) {
        setError('Lesson CNY price must be a positive number, or blank to use the course default');
        return;
      }
    }
    setSavingPriceId(v.id);
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      await courseApi.hostUpdateVideoPrice(id, v.id, { price, priceCny }, token);
      setNotice('Lesson price saved.');
      await loadVideos(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save lesson price');
    } finally {
      setSavingPriceId(null);
    }
  };

  const uploadTo = async (videoId: string, file: File) => {
    if (!id) return;
    setError(null);
    setUploadingId(videoId);
    setProgress(0);
    try {
      const token = await getToken();
      if (!token) return;
      await courseApi.uploadVideoFile(id, videoId, file, token, setProgress);
      await loadVideos(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingId(null);
      setProgress(0);
    }
  };

  const replace = async (videoId: string) => {
    if (!id) return;
    try {
      const token = await getToken();
      if (!token) return;
      await courseApi.hostCreateReplacement(id, videoId, token);
      setNotice(
        'A new version slot was created. Upload the replacement; the live video keeps playing until the new one is approved.'
      );
      await loadVideos(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start replacement');
    }
  };

  const submit = async () => {
    if (!id) return;
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      const res = await courseApi.hostPublishCourse(id, token);
      setStatus(res.status);
      setNotice(
        res.status === 'APPROVED'
          ? 'Course is now live and purchasable.'
          : 'Submitted for review. The course goes live automatically once all lessons are approved.'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit');
    }
  };

  const withdraw = async () => {
    if (!id) return;
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      const res = await courseApi.hostWithdrawCourse(id, token);
      setStatus(res.status);
      setNotice('Withdrawn from review. You can keep editing.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to withdraw');
    }
  };

  /** Uploads a picture for the description text and returns its public URL. */
  const uploadDescriptionImage = async (file: File): Promise<string> => {
    setError(null);
    const token = await getToken();
    if (!token) {
      throw new Error('Your session expired. Please sign in again, then retry the upload.');
    }
    return courseApi.uploadCourseImage(file, token);
  };

  const addImages = async (files: File[]) => {
    setUploadingImages(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) {
        setError('Your session expired. Please sign in again, then retry the upload.');
        return;
      }
      for (const file of files) {
        const url = await courseApi.uploadCourseImage(file, token);
        setImages((prev) => [...prev, { imageUrl: url, isCover: prev.length === 0 }]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const setCover = (index: number) =>
    setImages((prev) => prev.map((img, i) => ({ ...img, isCover: i === index })));

  const removeImage = (index: number) =>
    setImages((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (prev[index].isCover && next.length > 0) next[0] = { ...next[0], isCover: true };
      return next;
    });

  const unpublish = async () => {
    if (!id) return;
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      const res = await courseApi.hostUnpublishCourse(id, token);
      setStatus(res.status);
      setNotice('Course is offline. Existing buyers keep access; no new purchases.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unpublish');
    }
  };

  // Only current (non-superseded) lessons are shown/managed; old versions
  // remain for existing buyers but are not editable here.
  const currentVideos = videos.filter((v) => !v.supersededAt);

  return (
    <AdminLayout
      title={isEdit ? 'Edit Course' : 'New Course'}
      navItems={user?.isAdmin ? undefined : hostNavItems}
      headerRight={
        // Preview the course detail page. The detail page is unlisted: reachable
        // by slug in any status, so the host can preview the layout before
        // publishing. Live courses say "View"; others say "Preview".
        isEdit && slug ? (
          <Link
            to={`/courses/${slug}`}
            target="_blank"
            className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm transition-colors hover:bg-gray-50"
            {...tourAttr(TOUR.cePreview)}
          >
            {status === 'APPROVED' || status === 'ACTIVE' ? 'View' : 'Preview'}
          </Link>
        ) : undefined
      }
    >
      <div className="mx-auto max-w-2xl">
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          <>
            {error && <p className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}
            {notice && (
              <p className="mb-4 rounded bg-green-50 p-3 text-sm text-green-700">{notice}</p>
            )}

            <div
              className="space-y-4 rounded-lg bg-white p-6 shadow-sm"
              {...tourAttr(TOUR.ceTitle)}
            >
              {!isEdit && user?.isAdmin && (
                <Field label="Host (owner of this course)" required>
                  <select
                    className={inputCls}
                    value={hostId}
                    onChange={(e) => setHostId(e.target.value)}
                  >
                    <option value="">Select a host</option>
                    {hostOptions.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.displayName}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <BilingualField
                fieldType="courseTitle"
                label="Title"
                required
                value={title}
                onChange={setTitle}
                inputClassName={inputCls}
              />
              <DescriptionEditor
                fieldType="courseDescription"
                label="Description"
                rows={4}
                value={description}
                onChange={setDescription}
                inputClassName={inputCls}
                placeholder={{
                  ZH: '显示在课程详情页。',
                  EN: 'Shown on the course detail page.',
                }}
                uploadImage={uploadDescriptionImage}
                onUploadingChange={setUploadingDescImage}
                onUploadError={setError}
                lineBreaks
              />
              <div className="grid grid-cols-2 gap-4" {...tourAttr(TOUR.cePrice)}>
                <Field label="Default lesson price USD" required>
                  <input
                    className={inputCls}
                    inputMode="decimal"
                    placeholder="49.00"
                    value={priceUsd}
                    onChange={(e) => setPriceUsd(e.target.value)}
                  />
                </Field>
                <Field label="Default lesson price CNY">
                  <input
                    className={inputCls}
                    inputMode="decimal"
                    placeholder="300.00"
                    value={priceCny}
                    onChange={(e) => setPriceCny(e.target.value)}
                  />
                </Field>
              </div>
              <p className="text-xs text-gray-500">
                This is the default price for each lesson. You can override the price per lesson
                below. CNY prices are currently not used at checkout while WeChat Pay is
                unavailable, so all lessons are charged in USD.
              </p>
              <div className="mb-4 grid grid-cols-2 gap-4">
                <Field label="Who takes part" required>
                  <select
                    className={inputCls}
                    value={participation}
                    onChange={(e) => {
                      const next = e.target.value as ParticipationValue;
                      setParticipation(next);
                      if (next === 'ADULT_ONLY') {
                        setAgeMin('18');
                        setAgeMax('99');
                      }
                    }}
                  >
                    <option value="ADULT_ONLY">Adults only (parent courses)</option>
                    <option value="PARENT_CHILD">Parent and child</option>
                    <option value="KID_ONLY">Kids only</option>
                  </select>
                </Field>
                <Field label="Teaching language" required>
                  <select
                    className={inputCls}
                    value={language}
                    onChange={(e) => setLanguage(e.target.value as ActivityLanguageValue)}
                  >
                    <option value="ZH">Chinese</option>
                    <option value="EN">English</option>
                    <option value="BILINGUAL">Bilingual</option>
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4" {...tourAttr(TOUR.ceAge)}>
                <Field label="Min age" required>
                  <input
                    className={inputCls}
                    inputMode="numeric"
                    placeholder="0"
                    value={ageMin}
                    onChange={(e) => setAgeMin(e.target.value)}
                  />
                </Field>
                <Field label="Max age" required>
                  <input
                    className={inputCls}
                    inputMode="numeric"
                    placeholder="99"
                    value={ageMax}
                    onChange={(e) => setAgeMax(e.target.value)}
                  />
                </Field>
              </div>
            </div>

            <div className="mt-6 rounded-lg bg-white p-6 shadow-sm" {...tourAttr(TOUR.ceImages)}>
              <h2 className="mb-4 text-lg font-semibold text-gray-900">Images</h2>
              <div className="mb-2 grid grid-cols-2 gap-4 md:grid-cols-4">
                {images.map((img, i) => (
                  <div key={img.imageUrl} className="group relative">
                    <img
                      src={img.imageUrl}
                      alt=""
                      className={`h-32 w-full rounded-lg object-cover ${
                        img.isCover ? 'ring-2 ring-orange-500' : ''
                      }`}
                    />
                    <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-black bg-opacity-50 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                      <button
                        type="button"
                        onClick={() => setCover(i)}
                        className="rounded bg-white px-2 py-1 text-xs hover:bg-gray-100"
                      >
                        {img.isCover ? 'Cover' : 'Set cover'}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                    {img.isCover && (
                      <span className="absolute left-2 top-2 rounded bg-orange-500 px-2 py-1 text-xs text-white">
                        Cover
                      </span>
                    )}
                  </div>
                ))}
                <label className="flex h-32 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 transition-colors hover:border-orange-500 hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={uploadingImages}
                    onChange={(e) => {
                      // Snapshot the FileList synchronously: resetting e.target.value
                      // below clears the live FileList before addImages' async body reads it.
                      const files = e.target.files ? Array.from(e.target.files) : [];
                      if (files.length) void addImages(files);
                      e.target.value = '';
                    }}
                  />
                  {uploadingImages ? (
                    <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-orange-500"></div>
                  ) : (
                    <>
                      <span className="mb-1 text-2xl text-gray-400">+</span>
                      <span className="text-sm text-gray-500">Upload</span>
                    </>
                  )}
                </label>
              </div>
              <p className="text-xs text-gray-500">
                Supported formats: JPG, PNG, WEBP. Click &quot;Set Cover&quot; to choose cover
                image.
              </p>
              <p className="mt-1 text-xs text-gray-500">
                These are the course cover and intro images shown on the detail page only; the
                actual course videos are uploaded as lessons below.
              </p>
            </div>

            <button
              onClick={saveCourse}
              disabled={saving || uploadingImages || uploadingDescImage}
              {...tourAttr(TOUR.ceSave)}
              className="mt-6 rounded-lg bg-orange-500 px-5 py-2 font-medium text-white hover:bg-orange-600 disabled:opacity-60"
            >
              {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create course'}
            </button>

            <div className="mt-6 rounded-lg bg-white p-6 shadow-sm" {...tourAttr(TOUR.ceLessons)}>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Lessons</h2>
                {isEdit && (
                  <div
                    className="flex items-center gap-3 text-sm"
                    {...tourAttr(TOUR.ceLessonStatus)}
                  >
                    <span
                      className={`rounded px-2 py-1 text-xs font-medium ${courseStatus(status).cls}`}
                    >
                      {courseStatus(status).label}
                    </span>
                    {status === 'APPROVED' || status === 'ACTIVE' ? (
                      <button onClick={unpublish} className="text-gray-500 hover:underline">
                        Take offline
                      </button>
                    ) : status === 'PENDING' ? (
                      <button onClick={withdraw} className="text-gray-500 hover:underline">
                        Withdraw
                      </button>
                    ) : (
                      <button
                        onClick={submit}
                        disabled={currentVideos.length === 0}
                        title={
                          currentVideos.length === 0 ? 'Add at least one lesson first' : undefined
                        }
                        className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        Set active
                      </button>
                    )}
                  </div>
                )}
              </div>

              {!isEdit ? (
                <p className="text-sm text-gray-500">
                  Save the course first, then add lessons (the actual course videos) here.
                </p>
              ) : (
                <>
                  <p className="mb-4 text-sm text-gray-500">
                    {status === 'PENDING'
                      ? 'In review. The course goes live automatically once all lessons are approved.'
                      : canActivate
                        ? 'All lessons are ready and approved.'
                        : 'All lessons must be approved before the course can go live.'}
                  </p>

                  {currentVideos.length === 0 ? (
                    <div className="py-8 text-center text-gray-500">
                      No lessons yet. Add one below to get started.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {currentVideos.map((v) => {
                        // A rejected lesson's video was deleted on reject, so its
                        // old file name is meaningless; show a prominent rejection
                        // notice and a re-upload action instead.
                        const isRejected = v.reviewStatus === 'rejected';
                        return (
                          <div key={v.id} className="rounded-lg border border-gray-200 p-4">
                            {renamingId === v.id ? (
                              <div className="mb-2 space-y-2">
                                <BilingualField
                                  compact
                                  fieldType="lessonTitle"
                                  label="Lesson title"
                                  required
                                  value={renameDraft}
                                  onChange={setRenameDraft}
                                  translateOnMount
                                  inputClassName={inputCls}
                                />
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={saveRename}
                                    disabled={savingRename}
                                    className="rounded-lg bg-gray-900 px-3 py-1 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                                  >
                                    {savingRename ? 'Saving...' : 'Save title'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setRenamingId(null)}
                                    className="rounded-lg border border-gray-300 px-3 py-1 text-sm font-medium hover:bg-gray-50"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="mb-1 flex items-center justify-between gap-3">
                                <span className="font-medium text-gray-900">
                                  {bilingualLines(v.titleCN, v.titleEN, siteLanguage)[0]}
                                  {bilingualLines(v.titleCN, v.titleEN, siteLanguage)[1] && (
                                    <span className="ml-2 text-sm font-normal text-gray-500">
                                      {bilingualLines(v.titleCN, v.titleEN, siteLanguage)[1]}
                                    </span>
                                  )}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => startRename(v)}
                                  className="text-sm text-orange-600 hover:underline"
                                >
                                  Rename
                                </button>
                              </div>
                            )}
                            {!isRejected && v.sourceFileName && (
                              <p className="mb-2 text-xs text-gray-400">{v.sourceFileName}</p>
                            )}

                            {uploadingId === v.id ? (
                              <>
                                <div className="h-2 w-full overflow-hidden rounded bg-gray-100">
                                  <div
                                    className="h-full bg-orange-500 transition-all"
                                    style={{ width: `${Math.round(progress * 100)}%` }}
                                  />
                                </div>
                                <p className="mt-1.5 text-xs text-gray-500">
                                  Uploading. Please keep this page open until it finishes.
                                </p>
                              </>
                            ) : isRejected ? (
                              <div className="my-2 flex flex-col items-center gap-2 rounded-md bg-red-50 px-4 py-5 text-center">
                                <p className="text-sm font-semibold text-red-600">
                                  Rejected{v.rejectionReason ? `: ${v.rejectionReason}` : ''}
                                </p>
                                <p className="text-xs text-gray-500">
                                  This video was removed. Upload a new file to resubmit it for
                                  review.
                                </p>
                                <label className="mt-1 inline-block cursor-pointer text-sm font-medium text-orange-600 hover:underline">
                                  Re-upload video file
                                  <input
                                    type="file"
                                    accept="video/mp4,video/quicktime,video/webm"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) void uploadTo(v.id, file);
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                              </div>
                            ) : v.status === 'failed' ? (
                              <div className="text-sm text-red-600">
                                Video processing failed.{' '}
                                <label className="cursor-pointer font-medium text-orange-600 hover:underline">
                                  Re-upload video file
                                  <input
                                    type="file"
                                    accept="video/mp4,video/quicktime,video/webm"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) void uploadTo(v.id, file);
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                              </div>
                            ) : v.status === 'importing' ? (
                              <label className="inline-block cursor-pointer text-sm text-orange-600 hover:underline">
                                Upload video file
                                <input
                                  type="file"
                                  accept="video/mp4,video/quicktime,video/webm"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) void uploadTo(v.id, file);
                                    e.target.value = '';
                                  }}
                                />
                              </label>
                            ) : (
                              // Uploaded (processing or ready). Everything after the
                              // upload happens in Mux + admin review, so we just
                              // confirm the upload and don't surface that churn.
                              <div className="flex items-center gap-3 text-sm">
                                <span className="font-medium text-green-600">
                                  Upload successful
                                </span>
                                {v.status === 'ready' && v.reviewStatus === 'approved' && (
                                  <button
                                    onClick={() => replace(v.id)}
                                    className="text-orange-600 hover:underline"
                                  >
                                    Replace this video
                                  </button>
                                )}
                              </div>
                            )}

                            {previews[v.id]?.playbackId && previews[v.id]?.token && (
                              <button
                                type="button"
                                onClick={() =>
                                  setPreviewing({
                                    title: localize(v.titleCN, v.titleEN, siteLanguage),
                                    playbackId: previews[v.id].playbackId as string,
                                    token: previews[v.id].token as string,
                                  })
                                }
                                className="ml-3 text-sm text-orange-600 hover:underline"
                              >
                                Preview
                              </button>
                            )}

                            <div className="mt-3 flex flex-wrap items-end gap-2 pt-3">
                              <div>
                                <label className="block text-xs text-gray-500">Price USD</label>
                                <input
                                  className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
                                  inputMode="decimal"
                                  placeholder={priceUsd || 'default'}
                                  value={priceEdits[v.id]?.usd ?? ''}
                                  onChange={(e) =>
                                    setPriceEdits((prev) => ({
                                      ...prev,
                                      [v.id]: {
                                        usd: e.target.value,
                                        cny: prev[v.id]?.cny ?? '',
                                      },
                                    }))
                                  }
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500">Price CNY</label>
                                <input
                                  className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
                                  inputMode="decimal"
                                  placeholder={priceCny || 'default'}
                                  value={priceEdits[v.id]?.cny ?? ''}
                                  onChange={(e) =>
                                    setPriceEdits((prev) => ({
                                      ...prev,
                                      [v.id]: {
                                        usd: prev[v.id]?.usd ?? '',
                                        cny: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              </div>
                              <button
                                onClick={() => savePrice(v)}
                                disabled={savingPriceId === v.id}
                                className="rounded-lg border border-gray-300 px-3 py-1 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                              >
                                {savingPriceId === v.id ? 'Saving...' : 'Save price'}
                              </button>
                              <span className="text-xs text-gray-400">
                                Leave blank to use the course default.
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
                    <BilingualField
                      compact
                      className="flex-1"
                      fieldType="lessonTitle"
                      label="New lesson title"
                      value={newTitle}
                      onChange={setNewTitle}
                      translateOnMount={false}
                      placeholder={{ ZH: '例如：第一课', EN: 'e.g. Lesson 1' }}
                      inputClassName={inputCls}
                    />
                    <button
                      onClick={addSegment}
                      disabled={!hasBilingualValue(newTitle) || addingSegment}
                      className="whitespace-nowrap rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                    >
                      {addingSegment ? 'Adding...' : 'Add lesson'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {previewing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPreviewing(null)}
        >
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between text-white">
              <span className="text-sm font-medium">{previewing.title}</span>
              <button
                onClick={() => setPreviewing(null)}
                className="rounded px-2 py-1 text-sm hover:bg-white/10"
              >
                Close
              </button>
            </div>
            <MuxPlayer
              playbackId={previewing.playbackId}
              tokens={{ playback: previewing.token }}
              streamType="on-demand"
              accentColor="#f97316"
              autoPlay
              {...(muxEnvKey ? { envKey: muxEnvKey } : {})}
              style={{ width: '100%', aspectRatio: '16 / 9' }}
            />
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const inputCls =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none';

function courseStatus(status: string): { label: string; cls: string } {
  switch (status) {
    case 'APPROVED':
    case 'ACTIVE':
      return { label: 'Live', cls: 'bg-green-100 text-green-700' };
    case 'PENDING':
      return { label: 'In review', cls: 'bg-yellow-100 text-yellow-700' };
    case 'REJECTED':
      return { label: 'Rejected', cls: 'bg-red-100 text-red-700' };
    case 'INACTIVE':
      return { label: 'Offline', cls: 'bg-gray-100 text-gray-600' };
    default:
      return { label: 'Draft', cls: 'bg-gray-100 text-gray-600' };
  }
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}
